// Relative imports only, never the package barrel: an app evaluates this before its early error listener exists. ./early-import-wall.test.ts enforces it.
import {
  clientCoreFields,
  type ClientLogging,
  createClientLogging,
} from "./client-logging";
import {
  createEarlyErrorCapture,
  type EarlyErrorCapture,
} from "./early-error-capture";
import { createPageBackend } from "./page-backend";
import {
  createSessionIdSource,
  SESSION_ID_KEY_SUFFIX,
  type SessionIdSource,
} from "./session-id";
import {
  createVisitIdSource,
  VISIT_ID_KEY_SUFFIX,
  VISIT_LAST_SEEN_KEY_SUFFIX,
  type VisitIdSource,
} from "./visit-id";

export interface AppLoggingConfig {
  readonly serviceName: string;
  // Persisted ids live under these keys; changing the prefix rotates every browser's id at once.
  readonly storageKey: (suffix: string) => string;
  readonly redactedRoutes?: readonly string[];
  readonly environment: {
    readonly mode: string;
    readonly appVersion: unknown;
    readonly debug: boolean;
    // A getter because the page backend reads it once, on first use.
    readonly readBackendSetting: () => unknown;
  };
}

export interface AppLogging {
  readonly logging: ClientLogging;
  readonly capture: EarlyErrorCapture;
  readonly visitIds: VisitIdSource;
  readonly sessionIds: SessionIdSource;
}

const BACKEND_SETTING_NAME = "VITE_LOG_BACKEND";

// One page backend, session source and visit source shared by the early capture and the logger, so both agree on ids and on a single worker queue.
export function createAppLogging(config: AppLoggingConfig): AppLogging {
  const sessionIds = createSessionIdSource(
    config.storageKey(SESSION_ID_KEY_SUFFIX),
  );
  const visitIds = createVisitIdSource({
    idKey: config.storageKey(VISIT_ID_KEY_SUFFIX),
    lastSeenKey: config.storageKey(VISIT_LAST_SEEN_KEY_SUFFIX),
  });
  const shared = {
    redactedRoutes: config.redactedRoutes,
    coreFields: clientCoreFields(
      config.serviceName,
      config.environment.mode,
      config.environment.appVersion,
    ),
    getBackend: createPageBackend(
      config.environment.readBackendSetting,
      BACKEND_SETTING_NAME,
    ),
    getSessionId: () => sessionIds.getSessionId(),
    getVisitId: () => visitIds.getVisitId(),
  };
  return {
    logging: createClientLogging({
      ...shared,
      debug: config.environment.debug,
    }),
    capture: createEarlyErrorCapture(shared),
    visitIds,
    sessionIds,
  };
}
